<template>
  <div class="action-bar">
    <button @click="back"
            :class="{hidden: !displayBackButton}"
            class="nxt-button action-bar__button--back"
    >
      {{ backButtonText }}
    </button>
    <div class="nxt-button action-bar__button--advice"
         @click="openAdvice"
         @animationend="animateAdvice = false"
         :class="{invisible: !hasAdvice, animated: animateAdvice}">
      <font-awesome-icon
        :icon="lightBulbIcon"
      />
    </div>
    <div class="action-bar__button--right">
      <button v-if="$route.name === 'validateConfirm'"
              :disabled="disableCancelButton"
              class="nxt-button action-bar__button--cancel --disabled"
              data-cy="cancel-button" @click="openCancelProject">
        {{ cancelButtonText }}
      </button>
      <button @click="next(false)"
              id="next-button"
              data-cy="next-button"
              class="nxt-button nxt-button__primary action-bar__button--next"
              v-if="isValidForm"
      >
        {{ nextButtonText }}
      </button>
    </div>
    <component
      v-if="hasMoreInfo"
      :advice="advice"
      v-bind:is="advice.modal"
      :visible.sync="showModal">
    </component>
    <modal v-if="!hasMoreInfo && advice.content.summary !== ''" :visible.sync="showModal">
      <div class="advice">
        <img v-if="advice.content.image"
             class="advice__image"
             :alt="advice.content.alt"
             :src="advice.content.image"
        />
        <font-awesome-icon
          v-else-if="advice.content.icon"
          :icon="advice.content.icon"
          class="advice__icon" />
        <font-awesome-icon
          v-else
          :icon="['fal', 'lightbulb-on']"
          class="advice__default_icon" />
        <div class="advice__title">
          {{$t(advice.content.title)}}
        </div>
        <div class="advice__summary"
             v-html="$t(advice.content.summary)">
        </div>
      </div>
    </modal>
    <modal :visible.sync="showWarningModal" height="fit-content" width="360px">
      <div class="special-warning">
        <font-awesome-icon class="special-warning__icon" :icon="['fal', 'exclamation-triangle']"/>
        <div class="special-warning__content">
          {{ $t('inventory.warningEmptyRooms') }}
        </div>
      </div>
      <template v-slot:footer>
        <div class="special-warning__footer">
          <button class="nxt-button" @click="showWarningModal = false;">
            {{ $t('base.cancel') }}
          </button>
          <button class="nxt-button nxt-button__primary" @click="proceedAfterWarning">
            {{ $t('base.continue') }}
          </button>
        </div>
      </template>
    </modal>
  </div>
</template>
<script>
import RouteManager from '@/routes/RouteManager';
import AdviceModal from '@/components/advice/modals/AdviceModal.vue';
import EventBus from '@/utils/helpers/event-bus';
import Modal from '@/components/utils/Modal.vue';
import '@/assets/scss/animations.scss';
import { isSpecialLogementType } from '@/store/utils';

export default {
  name: 'layout-action-bar',
  components: {
    Modal,
    AdviceModal,
  },
  data() {
    return {
      showModal: false,
      showCancelProjectModal: false,
      animateAdvice: false,
      customSaveStateFunction: null,
      showWarningModal: false,
    };
  },
  computed: {
    advice() {
      return this.$store.getters['adviceStore/advice'];
    },
    backButtonText() {
      if (this.isMobile) return `${this.$t('base.back')}`;
      return `< ${this.$t('base.back')}`;
    },
    width() {
      return this.$store.getters['dimensionStore/width'];
    },
    isMobile() {
      return this.$store.getters['dimensionStore/isMobile'];
    },
    displayBackButton() {
      return RouteManager.previous(this.$route.name) !== null;
    },
    isValidForm() {
      const storeRoute = `${this.parentRoute}Store/${this.childRoute}/isValidForm`;

      if (this.$store.getters[storeRoute] !== undefined) {
        return this.$store.getters[storeRoute];
      }
      return RouteManager.next(this.$route.name) !== null;
    },
    childRoute() {
      return this.$store.getters['routeStore/childRoute'];
    },
    parentRoute() {
      return this.$store.getters['routeStore/parentRoute'];
    },
    nameSaveStateFunction() {
      const saveStateFunc = this.customSaveStateFunction
        || `${this.parentRoute}Store/${this.childRoute}SaveState`;
      // eslint-disable-next-line no-underscore-dangle
      if (this.$store._actions[saveStateFunc] !== undefined) {
        return saveStateFunc;
      }
      return 'projectStore/emptyAction';
    },
    hasAdvice() {
      return this.advice.modal.length > 0 || this.advice.content.title.length > 0;
    },
    lightBulbIcon() {
      return ['fal', 'lightbulb-on'];
    },
    hasMoreInfo() {
      return this.advice && this.advice.modal && this.advice.modal.length > 0;
    },
    deliveryAddressNotKnown() {
      return (this.$route.name === 'deliveryAddress'
        && this.$store.getters['deliveryStore/isAddressKnown'] === false);
    },
    loadingRDC() {
      return (this.$route.name === 'loadingHousing'
        && this.checkFloor(this.$store.getters['loadingStore/floor'].floor));
    },
    deliveryRDC() {
      return (this.$route.name === 'deliveryHousing'
        && this.checkFloor(this.$store.getters['deliveryStore/floor'].floor));
    },
    loadingGM() {
      return (this.$route.name === 'loadingHousing'
        && isSpecialLogementType(this.$store.getters['loadingStore/floor'].type_logement));
    },
    deliveryGM() {
      return (this.$route.name === 'deliveryHousing'
        && isSpecialLogementType(this.$store.getters['deliveryStore/floor'].type_logement));
    },
    loadingGMBack() {
      return (this.$route.name === 'deliveryAddress'
        && isSpecialLogementType(this.$store.getters['loadingStore/floor'].type_logement));
    },
    deliveryGMBack() {
      return (this.$route.name === 'serviceOfferDates'
        && isSpecialLogementType(this.$store.getters['deliveryStore/floor'].type_logement));
    },
    addressNotKnownBack() {
      return (this.$route.name === 'serviceOfferDates'
        && !this.$store.getters['deliveryStore/isAddressKnown']);
    },
    loadingParkingBack() {
      return (this.$route.name === 'loadingParkingArea'
        && this.checkFloor(this.$store.getters['loadingStore/floor'].floor));
    },
    deliveryParkingBack() {
      return (this.$route.name === 'deliveryParkingArea'
        && this.checkFloor(this.$store.getters['deliveryStore/floor'].floor));
    },
    noBoxDelivery() {
      return !this.$store.getters['serviceOfferStore/cartons']
        || this.$store.getters['furnituresStore/boxNumberKnown'] === -1;
    },
    noBoxDeliveryNext() {
      return (this.parentRoute === 'furnitures'
        && this.childRoute === 'boxConfig'
        && this.noBoxDelivery);
    },
    noBoxDeliveryBack() {
      return (this.parentRoute === 'validate'
        && this.childRoute === 'confirm'
        && this.noBoxDelivery);
    },
    cancelButtonText() {
      return this.isMobile
        ? this.$t('base.cancel')
        : this.$t('validate.cancel');
    },
    disableCancelButton() {
      return this.$store.getters['projectStore/cancelStatus'];
    },
    nextButtonText() {
      if (this.$route.name === 'validateConfirm') {
        return this.isMobile
          ? this.$t('base.confirmMobile')
          : this.$t('base.confirm');
      }
      return this.$t('base.next');
    },
  },
  methods: {
    checkFloor(floor) {
      const floors = floor.split('+');
      const filter = floors.filter(f => f === '0');
      return floors.length === filter.length;
    },
    getDefaultAccessibilityValue() {
      return {
        floor: {
          all_in_lift: null,
          lift: null,
          lift_comment: '',
          mm_obligatoire: null,
          mm_utilisable: null,
          stairs: null,
        },
        parking: {
          type: '',
          portage: '',
          vl: null,
        },
      };
    },
    setDefaultAccessibilityValue(store, defaultData) {
      if (!defaultData) {
        defaultData = this.getDefaultAccessibilityValue();
      }
      this.$store.dispatch('projectStore/resetAccessValues', {
        store,
        data: defaultData,
      });
    },
    resetUserBoxes() {
      this.$store.commit('furnituresStore/SET_USER_CARTON', {
        key: 'cartons_std',
        value: 0,
      });
      this.$store.commit('furnituresStore/SET_USER_CARTON', {
        key: 'cartons_livre',
        value: 0,
      });
      this.$store.commit('furnituresStore/SET_USER_CARTON', {
        key: 'cartons_penderie',
        value: 0,
      });
    },
    handleSpecialCaseNext() {
      if (this.deliveryAddressNotKnown) {
        const defaultData = this.getDefaultAccessibilityValue();
        defaultData.floor.floor = '';
        defaultData.floor.cave = null;
        this.setDefaultAccessibilityValue('deliveryStore', defaultData);
        return 'serviceOfferDates';
      }
      if (this.noBoxDeliveryNext) {
        this.resetUserBoxes();
        this.customSaveStateFunction = 'furnituresStore/boxDeliverySaveState';
        return 'validateConfirm';
      }
      if (this.loadingGM) {
        this.setDefaultAccessibilityValue('loadingStore');
        return 'deliveryAddress';
      }
      if (this.deliveryGM) {
        this.setDefaultAccessibilityValue('deliveryStore');
        return 'serviceOfferDates';
      }
      if (this.loadingRDC) {
        const defaultData = this.getDefaultAccessibilityValue();
        defaultData.parking = undefined;
        this.setDefaultAccessibilityValue('loadingStore', defaultData);
        return 'loadingParkingArea';
      }
      if (this.deliveryRDC) {
        const defaultData = this.getDefaultAccessibilityValue();
        defaultData.parking = undefined;
        this.setDefaultAccessibilityValue('deliveryStore', defaultData);
        return 'deliveryParkingArea';
      }
      return null;
    },
    handleSpecialCaseBack() {
      if (this.addressNotKnownBack) return 'deliveryAddress';
      if (this.loadingParkingBack) return 'loadingHousing';
      if (this.deliveryParkingBack) return 'deliveryHousing';
      if (this.noBoxDeliveryBack) return 'furnituresBoxConfig';
      if (this.loadingGMBack) return 'loadingHousing';
      if (this.deliveryGMBack) return 'deliveryHousing';
      return null;
    },
    handleSpecialWarning() {
      if (this.$route.name === 'furnituresInventory') {
        return this.$store.getters['furnituresStore/hasEmptyRooms'];
      }
      return false;
    },
    proceedAfterWarning() {
      this.showWarningModal = false;
      this.next(true);
    },
    back() {
      const backRoute = RouteManager.getCurrentRoute(this.handleSpecialCaseBack())
        || RouteManager.previous(this.$route.name);
      if (backRoute) {
        this.$router.push({
          name: backRoute.name,
        });
      }
    },
    next(ignoreWarning) {
      if (!ignoreWarning && this.handleSpecialWarning()) {
        this.showWarningModal = true;
        return;
      }

      const nextRoute = RouteManager.getCurrentRoute(this.handleSpecialCaseNext())
        || RouteManager.next(this.$route.name);

      if (nextRoute && nextRoute.sync) {
        this.$store.commit('projectStore/SET_DATA_SAVING', true);
      }

      this.$store.dispatch(this.nameSaveStateFunction).then(() => {
        if (nextRoute && nextRoute.newStep) {
          this.$store.dispatch('projectStore/setStep', nextRoute.newStep);
        }
      }).finally(() => {
        if (nextRoute && nextRoute.sync) {
          this.$store.commit('projectStore/SET_DATA_SAVING', false);
          this.$router.push({
            name: nextRoute.name,
          });
        }
      });

      // Reset customSaveStateFunction
      this.customSaveStateFunction = null;

      if (nextRoute && !nextRoute.sync) {
        this.$router.push({
          name: nextRoute.name,
        });
      }
    },
    openAdvice() {
      if (this.hasAdvice) this.showModal = !this.showModal;
    },
    openCancelProject() {
      EventBus.$emit('openCancelProject');
    },
  },
  watch: {
    $route: {
      handler(value) {
        const routes = RouteManager.getRouteComposition(value.name);
        this.$store.commit('adviceStore/UNSET_ADVICE');
        this.$store.commit('routeStore/SET_PARENT_ROUTE', routes && routes.parentRoute);
        this.$store.commit('routeStore/SET_CHILD_ROUTE', routes && routes.childRoute);
      },
      immediate: true,
    },
    advice() {
      this.animateAdvice = this.hasAdvice;
    },
  },
};
</script>
<style scoped lang="scss">
  .invisible{
   visibility: hidden;
  }
  .action-bar {
    padding: 0 10px;
    display: grid;
    align-items: center;
    column-gap: 12px;
    grid-template-columns: auto auto minmax(0, 1fr);
    @include breakpoint(desktop){
      grid-template-columns: auto auto;
    }

    &__button {

      &--right {
        width: 100%;
        justify-self: end;
        display: flex;
        justify-content: flex-end;
      }

      &--back {
        display: block;
        text-align: left;
        background-color: white;
      }

      &--cancel {
        padding: 8px 20px;
        margin-right: 15px;
        @include breakpoint(mobile) {
          width: auto;
          min-width: 140px;
        }
      }

      &--next {
        flex: 1;
        justify-self: end;
        padding: 8px 20px;
        width: 100%;
        @include breakpoint(mobile) {
          flex: unset;
          width: auto;
          min-width: 140px;
        }
      }

      &--advice {
        display: grid;
        height: 42px;
        width: 42px;
        place-content: center;
        place-self: center;
        align-content: center;
        align-self: center;
        background: $--color-light-grey-blue;
        border-radius: $--border-radius-base;
        &--active {
          background: $--color-primary;
        }
        @include breakpoint(desktop){
          display: none;
        }
        &.animated {
          animation: bounce-in .6s;
        }
      }
    }
  }
  .advice {
    overflow: auto;
    &__image {
      display: block;
      width: 50%;
      margin: 14px auto 32px auto;
    }
    &__title {
      font-size: 22px;
      text-align: center;
      font-weight: bold;
      margin: 20px 0;
    }
    &__icon {
      display: block;
      width: 60px;
      height: 60px;
      margin: auto;
    }
    &__default_icon {
      display: block;
      width: 42px;
      height: 42px;
      margin: auto;
    }
  }
  .special-warning {
    &__icon {
      width: 42px;
      height: 42px;
      margin: 0 auto 20px auto;
      display: block;
    }
    &__content {
      text-align: center;
    }
    &__footer {
      margin-top: 20px;
      display: flex;
      flex-direction: row;
      justify-content: flex-end;

      .nxt-button {
        margin-left: 10px;
        padding: 10px 20px;
      }
    }
  }

</style>
